
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormData } from './types';

interface BreedingValuesFormProps {
  formData: FormData;
  onInputChange: (field: string, value: string) => void;
}

const BreedingValuesForm: React.FC<BreedingValuesFormProps> = ({ formData, onInputChange }) => {
  const evaluationFields = [
    { key: 'calving_ease', label: 'Calving Ease' },
    { key: 'birth_weight', label: 'Birth Weight', hasUnit: true, unitKey: 'birth_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'weaning_weight', label: 'Weaning Weight', hasUnit: true, unitKey: 'weaning_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'yearling_weight', label: 'Yearling Weight', hasUnit: true, unitKey: 'yearling_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'milk', label: 'Milk / Maternal Milk', hasUnit: true, unitKey: 'milk_unit', unitOptions: ['kg', 'lb'] },
    { key: 'total_maternal', label: 'Total Maternal', hasUnit: true, unitKey: 'total_maternal_unit', unitOptions: ['kg', 'lb'] },
    { key: 'carcass_weight', label: 'Carcass Weight', hasUnit: true, unitKey: 'carcass_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'marbling', label: 'Marbling' },
    { key: 'intramuscular_fat', label: 'Intramuscular Fat' },
    { key: 'fat_thickness', label: 'Fat Thickness', hasUnit: true, unitKey: 'fat_thickness_unit', unitOptions: ['mm', 'in'] },
    { key: 'maternal_weaning_weight', label: 'Maternal Weaning Weight', hasUnit: true, unitKey: 'maternal_weaning_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'ribeye_area', label: 'Ribeye Area', hasUnit: true, unitKey: 'ribeye_area_unit', unitOptions: ['sq cm', 'sq in'] },
    { key: 'gestation_length', label: 'Gestation Length' },
    { key: 'scrotal_size', label: 'Scrotal Size / Circumference', hasUnit: true, unitKey: 'scrotal_size_unit', unitOptions: ['cm', 'in'] },
    { key: 'day_200_weight', label: '200 Day Weight', hasUnit: true, unitKey: 'day_200_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'day_400_weight', label: '400 Day Weight', hasUnit: true, unitKey: 'day_400_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'day_600_weight', label: '600 Day Weight', hasUnit: true, unitKey: 'day_600_weight_unit', unitOptions: ['kg', 'lb'] },
    { key: 'eye_muscle_area', label: 'Eye Muscle Area', hasUnit: true, unitKey: 'eye_muscle_area_unit', unitOptions: ['sq cm', 'sq in'] },
    { key: 'rump_fat', label: 'Rump Fat', hasUnit: true, unitKey: 'rump_fat_unit', unitOptions: ['mm', 'in'] },
    { key: 'retail_beef_yield', label: 'Retail Beef Yield' }
  ];

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
      <div className="flex items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold">Breeding Values</h3>
        <Select value={formData.evaluation_type} onValueChange={(value) => onInputChange('evaluation_type', value)}>
          <SelectTrigger className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="EPD">EPD</SelectItem>
            <SelectItem value="EBV">EBV</SelectItem>
            <SelectItem value="NA">NA</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {evaluationFields.map((field) => {
          const accKey = `${field.key}_acc` as keyof FormData;
          const percRankKey = `${field.key}_perc_rank` as keyof FormData;
          return (
            <div key={field.key}>
              <Label htmlFor={field.key}>{field.label}</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id={field.key}
                  type="number"
                  max="100000"
                  step="any"
                  value={formData[field.key as keyof FormData]}
                  onChange={(e) => onInputChange(field.key, e.target.value)}
                  placeholder="Value"
                  className="flex-1 min-w-0"
                />
                {field.hasUnit && (
                  <Select 
                    value={formData[field.unitKey as keyof FormData]} 
                    onValueChange={(value) => onInputChange(field.unitKey!, value)}
                  >
                    <SelectTrigger className="w-20 flex-shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {field.unitOptions?.map((unit) => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Input
                  type="number"
                  step="any"
                  value={formData[accKey]}
                  onChange={(e) => onInputChange(accKey, e.target.value)}
                  placeholder="ACC"
                  className="flex-1 min-w-0"
                />
                <Input
                  type="number"
                  step="any"
                  value={formData[percRankKey]}
                  onChange={(e) => onInputChange(percRankKey, e.target.value)}
                  placeholder="% Rank"
                  className="flex-1 min-w-0"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BreedingValuesForm;
